<script lang="ts">
  import {ButtonColorScheme, ButtonVariant} from 'Types/shared/Button';
  import Tooltip from 'Components/Engage/Tooltip.svelte';

  export let colorScheme: ButtonColorScheme = ButtonColorScheme.PRIMARY;
  export let variant: ButtonVariant = ButtonVariant.BASE;
  export let fullWidth = false;
  export let isLoading: boolean = false;
  export let disabled: boolean = false;
  export let disabledTooltipText: string = '';

  $: showTooltip = disabled && disabledTooltipText.length > 0;
</script>

<Tooltip tooltipText={disabledTooltipText} {showTooltip}>
  <button
    {...$$restProps}
    class={`${colorScheme} ${variant} ${fullWidth ? 'full-width' : ''}`}
    disabled={disabled || isLoading}
    on:click
  >
    {#if isLoading}
      <span
        class="spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      />
      <span class="sr-only">Loading...</span>
    {:else}
      <slot />
    {/if}
  </button>
</Tooltip>

<style lang="postcss">
  button {
    align-items: center;
    align-self: flex-start;
    border-radius: 4px;
    border-style: solid;
    border-width: 1px;
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
    display: inline-flex;
    font-size: 0.875rem; /* 14px */
    font-weight: 500;
    justify-content: center;
    line-height: 1.4285714; /* 20 / 14 */
    padding: 10px 16px;
    transition: 0.2s;

    &:focus {
      box-shadow: inset 0 1px 0 rgb(255 255 255 / 15%),
        0 1px 1px rgb(0 0 0 / 8%), 0 0 0 0.2rem rgb(73 177 170 / 50%);
      outline: none;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &[aria-disabled='true'] {
      cursor: not-allowed;
      opacity: 0.6;
      background: #d0d5dd;
      border-color: #d0d5dd;
      color: #344054;
      box-shadow: none;

      &:hover,
      &:focus {
        background: #d0d5dd !important;
        border-color: #d0d5dd !important;
        color: #344054 !important;
      }
    }

    & > :global(*) + :global(*) {
      margin-left: 8px;
    }
  }

  .ghost {
    background: transparent;
    box-shadow: none;
  }

  /* TODO: Use CSS variables instead to avoid too much boilerplate as we add more colors in the future. */
  /* IMPORTANT: The name of the variant class MUST be included in the ButtonColorScheme enum WITHOUT the dot prefix.  */
  .primary {
    background: #0e9384;
    border-color: #0e9384;
    color: #fff;

    &:not([disabled]):hover,
    &:not([disabled]):focus {
      background: #1f7a74;
      border-color: #1f7a74;
    }

    &.ghost {
      background: transparent;
      border-color: transparent;
      color: #0e9384;

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        background: #b6deda;
      }
    }

    &.outline {
      background: transparent;
      border-color: #0e9384;
      color: #0e9384;

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        background: #0e9384;
        color: #fff;
      }
    }
  }

  .gray {
    background: #d0d5dd;
    border-color: #d0d5dd;
    color: #344054;

    &:not([disabled]):hover,
    &:not([disabled]):focus {
      background: #eaecf0;
      border-color: #eaecf0;
    }

    &.ghost {
      background: transparent;
      border-color: transparent;

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        background: #eaecf0;
        color: #344054;
      }
    }

    &.outline {
      background: transparent;
      color: #344054;

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        background: #d0d5dd;
      }
    }
  }

  .danger {
    background: #b42318;
    border-color: #b42318;
    color: #fff;

    &:hover,
    &:focus {
      background: #8f1c13;
      border-color: #8f1c13;
    }

    &.ghost {
      background: transparent;
      border-color: transparent;
      color: #b42318;

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        background: #fda29b;
      }
    }

    &.outline {
      background: transparent;
      border-color: #fda29b;
      color: #b42318;

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        background: #b42318;
        border-color: #b42318;
        color: #fff;
      }
    }
  }

  .full-width {
    width: 100%;
  }
</style>
